/**
 * KnowFit calculation engine.
 *
 * Pure domain logic: this file does not read the DOM or localStorage, fetch
 * products, redirect the browser, or modify the Shopify cart.
 *
 * Browser usage: window.KnowFitEngine.calculateProfile(profile)
 * Local tests:   require('../assets/knowfit-engine.js')
 */
(function initializeKnowFitEngine(root, factory) {
  const engine = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = engine;
  }

  if (root) {
    root.KnowFitEngine = engine;
  }
})(typeof window !== 'undefined' ? window : globalThis, function createKnowFitEngine() {
  'use strict';

  const ENGINE_VERSION = 16;
  const PROFILE_VERSION = 4;
  const MAX_DAYS_PER_WEEK = 7;
  const MAX_TRAINING_SESSIONS_PER_WEEK = 20;
  const CALCULATION_PRECISION = 6;
  const CATALOG_TIME_ZONE = 'Europe/Madrid';

  const GOAL_FACTORS = Object.freeze({
    lose_weight: 0.8,
    maintain: 1,
    gain_muscle: 1.1,
  });

  const PROTEIN_FACTORS = Object.freeze({
    lose_weight: 2,
    maintain: 1.8,
    gain_muscle: 1.8,
  });

  const ACTIVITY_FACTORS = Object.freeze({
    sedentary: 1.2,
    active: 1.4,
    very_active: 1.7,
  });

  const COVERAGE_FACTORS = Object.freeze({
    one_main: 0.3,
    two_mains: 0.6,
    full_day: 1,
  });

  const MEAL_CALORIE_DISTRIBUTION = Object.freeze({
    breakfast: 0.25,
    main: 0.30,
    snack: 0.15,
    dinner: 0.30,
  });

  const COVERAGE_MEAL_SLOTS = Object.freeze({
    one_main: Object.freeze(['main_or_dinner']),
    two_mains: Object.freeze(['main', 'dinner']),
    full_day: Object.freeze(['breakfast', 'main', 'snack', 'dinner']),
  });

  // Kept as a public compatibility constant for code that only needs the
  // number of compulsory dishes. The semantic source is COVERAGE_MEAL_SLOTS.
  const COVERAGE_MAIN_SLOTS = Object.freeze({
    one_main: 1,
    two_mains: 2,
    full_day: 4,
  });

  // TECH specifies ±5% for every goal; the merchant confirmed both nutrients.
  const GOAL_MARGIN_RULES = Object.freeze({
    lose_weight: Object.freeze({ ratio: 0.05, defined_in_spreadsheet: true }),
    maintain: Object.freeze({ ratio: 0.05, defined_in_spreadsheet: true }),
    gain_muscle: Object.freeze({ ratio: 0.05, defined_in_spreadsheet: true }),
  });

  const SELECTION_DEFAULTS = Object.freeze({
    meal_distribution_weight: 1,
    repeat_penalty_per_previous_use: 0.05,
    alternatives_per_day: 5,
  });

  // Selector calibration, not a change to nutritional targets: relative error
  // outside the confirmed ±5% tolerance costs twice as much for kcal.
  const NUTRITION_SCORE_WEIGHTS = Object.freeze({ kcal: 2, protein: 1 });

  const SUPPORTED_MEAL_TYPES = Object.freeze({
    main: true,
    dinner: true,
    breakfast: true,
    snack: true,
    extra: true,
  });

  const TRAINING_CALCULATION_ENABLED = true;
  const TRAINING_MODEL = 'weekly_average_v1';
  const TRAINING_SESSION_HOURS = 1;
  const TRAINING_CATEGORIES = Object.freeze({
    moderate: Object.freeze({ label: 'Fuerza o intensidad moderada', met: 5 }),
    high_intensity: Object.freeze({ label: 'Cardio o alta intensidad', met: 7 }),
    mixed: Object.freeze({ label: 'Variado', met: 6 }),
  });

  const GEB_COEFFICIENTS = Object.freeze({
    male: Object.freeze({
      base: 5,
      weight: 10,
      height: 6.25,
      age: 5,
    }),
    female: Object.freeze({
      base: -161,
      weight: 10,
      height: 6.25,
      age: 5,
    }),
  });

  class KnowFitValidationError extends Error {
    constructor(code, message, details = {}) {
      super(message);
      this.name = 'KnowFitValidationError';
      this.code = code;
      this.details = details;
    }
  }

  class KnowFitSelectionError extends Error {
    constructor(code, message, details = {}) {
      super(message);
      this.name = 'KnowFitSelectionError';
      this.code = code;
      this.details = details;
    }
  }

  function round(value, precision = CALCULATION_PRECISION) {
    const factor = 10 ** precision;
    return Math.round((value + Number.EPSILON) * factor) / factor;
  }

  function parseCalendarDate(value, field) {
    const match = typeof value === 'string' ? value.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
    const year = match ? Number(match[1]) : 0;
    const month = match ? Number(match[2]) : 0;
    const day = match ? Number(match[3]) : 0;
    const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const monthDays = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (!match || year < 1 || month < 1 || month > 12 || day < 1 || day > monthDays[month - 1]) {
      throw new KnowFitValidationError(
        field === 'birth_date' ? 'INVALID_BIRTH_DATE' : 'INVALID_REFERENCE_DATE',
        'Indica una fecha real en formato YYYY-MM-DD.',
        { field, value }
      );
    }
    return { year, month, day };
  }

  function getCurrentDate(inputDate = new Date()) {
    // Date-only input is already a calendar date; reject impossible dates
    // before Date can normalize them (for example, February 30 into March).
    if (typeof inputDate === 'string' && /^\d{4}-\d{2}-\d{2}(?:$|T)/.test(inputDate)) {
      parseCalendarDate(inputDate.slice(0, 10), 'referenceDate');
    }
    const date = inputDate instanceof Date ? inputDate
      : typeof inputDate === 'string' || typeof inputDate === 'number' ? new Date(inputDate) : new Date(NaN);
    if (Number.isNaN(date.getTime())) {
      throw new KnowFitValidationError('INVALID_REFERENCE_DATE', 'No se puede determinar la fecha actual.', { field: 'referenceDate' });
    }
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
      timeZone: CATALOG_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(date).map((part) => [part.type, part.value]));
    const result = `${parts.year.padStart(4, '0')}-${parts.month}-${parts.day}`;
    parseCalendarDate(result, 'referenceDate');
    return result;
  }

  function calculateAge(birthDate, referenceDate = getCurrentDate()) {
    if (birthDate === undefined || birthDate === null || birthDate === '') {
      throw new KnowFitValidationError('BIRTH_DATE_REQUIRED', 'Indica tu fecha de nacimiento para actualizar el plan.', { field: 'birth_date' });
    }
    const birth = parseCalendarDate(birthDate, 'birth_date');
    const reference = parseCalendarDate(referenceDate, 'referenceDate');
    if (birthDate > referenceDate) {
      throw new KnowFitValidationError('BIRTH_DATE_IN_FUTURE', 'La fecha de nacimiento no puede ser futura.', { field: 'birth_date', value: birthDate });
    }
    // The calendar comparison makes a February 29 birthday fall on March 1
    // in non-leap years, without fabricating a different stored birth date.
    const beforeBirthday = reference.month < birth.month || reference.month === birth.month && reference.day < birth.day;
    return reference.year - birth.year - (beforeBirthday ? 1 : 0);
  }

  function getCatalogCycleId(inputDate = new Date(), timeZone = CATALOG_TIME_ZONE) {
    const date = inputDate instanceof Date ? inputDate : new Date(inputDate);
    if (Number.isNaN(date.getTime())) return '';

    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(date).map((part) => [part.type, part.value])
    );
    const calendarDate = new Date(Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day)
    ));
    const daysSinceThursday = (calendarDate.getUTCDay() - 4 + 7) % 7;
    calendarDate.setUTCDate(calendarDate.getUTCDate() - daysSinceThursday);
    return calendarDate.toISOString().slice(0, 10);
  }

  function parseNumericValue(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;

    const trimmed = value.trim().replace(/\s+/g, '');
    if (!trimmed) return null;

    let normalized = trimmed;
    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');

    if (lastComma >= 0 && lastDot >= 0) {
      const decimalSeparator = lastComma > lastDot ? ',' : '.';
      const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';
      normalized = normalized.split(thousandsSeparator).join('');
      normalized = normalized.replace(decimalSeparator, '.');
    } else if (lastComma >= 0) {
      normalized = normalized.replace(',', '.');
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function requireEnum(value, allowedValues, field) {
    if (!Object.prototype.hasOwnProperty.call(allowedValues, value)) {
      throw new KnowFitValidationError(
        'INVALID_ENUM',
        `El valor de ${field} no es válido.`,
        { field, value, allowed: Object.keys(allowedValues) }
      );
    }

    return value;
  }

  function requirePositiveNumber(value, field) {
    const numericValue = typeof value === 'string' ? Number(value.trim()) : Number(value);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      throw new KnowFitValidationError(
        'INVALID_POSITIVE_NUMBER',
        `${field} debe ser un número mayor que cero.`,
        { field, value }
      );
    }

    return numericValue;
  }

  function requirePositiveInteger(value, field) {
    const numericValue = requirePositiveNumber(value, field);

    if (!Number.isInteger(numericValue)) {
      throw new KnowFitValidationError(
        'INVALID_POSITIVE_INTEGER',
        `${field} debe ser un número entero mayor que cero.`,
        { field, value }
      );
    }

    return numericValue;
  }

  function requireDays(value) {
    const days = Number(value);

    if (!Number.isInteger(days) || days < 1 || days > MAX_DAYS_PER_WEEK) {
      throw new KnowFitValidationError(
        'INVALID_DAYS_PER_WEEK',
        `days_per_week debe ser un entero entre 1 y ${MAX_DAYS_PER_WEEK}.`,
        { field: 'days_per_week', value }
      );
    }

    return days;
  }

  function migrateProfileDraft(rawProfile) {
    const profile = rawProfile && typeof rawProfile === 'object' && !Array.isArray(rawProfile)
      ? rawProfile
      : {};
    if (Number(profile.version) > PROFILE_VERSION) {
      throw new KnowFitValidationError(
        'UNSUPPORTED_PROFILE_VERSION',
        'Este perfil usa una versión más reciente. Recarga la página para continuar.',
        { version: profile.version }
      );
    }

    const draft = {};
    [
      'status', 'current_step', 'goal', 'sex', 'birth_date', 'age_years', 'age_calculated_on', 'weight',
      'height', 'activity_level', 'days_per_week', 'coverage_mode', 'eats_fish',
      'created_at', 'updated_at',
    ].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(profile, key)) draft[key] = profile[key];
    });

    // Only older contracts may supply aliases. An explicitly stored canonical
    // value, including an invalid one, must survive for validation/correction.
    if ([1, 2, 3].includes(profile.version)) {
      Object.entries({ sex: 'sex_for_bmr', weight: 'weight_kg', height: 'height_cm' }).forEach(([key, alias]) => {
        if (!Object.prototype.hasOwnProperty.call(profile, key)
          && Object.prototype.hasOwnProperty.call(profile, alias)) draft[key] = profile[alias];
      });
    }

    const isWeekly = [2, 3, PROFILE_VERSION].includes(profile.version) && profile.training_model === TRAINING_MODEL;
    const hasResponse = isWeekly && profile.training_collected === true
      && typeof profile.does_sport === 'boolean';
    const doesSport = hasResponse ? profile.does_sport : null;
    return {
      ...draft,
      version: PROFILE_VERSION,
      training_model: TRAINING_MODEL,
      training_collected: hasResponse,
      does_sport: doesSport,
      training_sessions_per_week: doesSport === false ? 0
        : doesSport === true ? profile.training_sessions_per_week ?? null : null,
      training_category: doesSport === true ? profile.training_category ?? null : null,
    };
  }

  function normalizeWeeklyTraining(profile) {
    if (Number(profile.version) > PROFILE_VERSION) {
      throw new KnowFitValidationError(
        'UNSUPPORTED_PROFILE_VERSION',
        'Este perfil usa una versión más reciente. Recarga la página para continuar.',
        { version: profile.version }
      );
    }
    if (profile.version !== PROFILE_VERSION || profile.training_model !== TRAINING_MODEL
      || profile.training_collected !== true || typeof profile.does_sport !== 'boolean') {
      throw new KnowFitValidationError(
        'TRAINING_RESPONSE_REQUIRED',
        'Responde si haces deporte para actualizar tu plan con el cálculo semanal.',
        { field: 'does_sport', training_model: TRAINING_MODEL }
      );
    }
    if (!profile.does_sport) {
      return { training_sessions_per_week: 0, training_category: null };
    }

    const rawSessions = profile.training_sessions_per_week;
    const sessions = parseNumericValue(rawSessions);
    if (!Number.isSafeInteger(sessions) || sessions <= 0 || sessions > MAX_TRAINING_SESSIONS_PER_WEEK) {
      throw new KnowFitValidationError(
        'INVALID_TRAINING_SESSIONS',
        `Indica entre 1 y ${MAX_TRAINING_SESSIONS_PER_WEEK} sesiones semanales completas (una hora por sesión).`,
        { field: 'training_sessions_per_week', value: rawSessions }
      );
    }
    return {
      training_sessions_per_week: sessions,
      training_category: requireEnum(profile.training_category, TRAINING_CATEGORIES, 'training_category'),
    };
  }

  function normalizeProfile(profile, { referenceDate = getCurrentDate() } = {}) {
    if (!profile || typeof profile !== 'object' || Array.isArray(profile)) {
      throw new KnowFitValidationError(
        'INVALID_PROFILE',
        'El perfil KnowFit debe ser un objeto.'
      );
    }

    if (Number(profile.version) > PROFILE_VERSION) {
      throw new KnowFitValidationError('UNSUPPORTED_PROFILE_VERSION', 'Este perfil usa una versión más reciente. Recarga la página para continuar.', { version: profile.version });
    }
    // v3 already collected birth date and weekly training. Renaming its three
    // personal fields does not require a new response or change its formulas.
    if (profile.version === 3) profile = migrateProfileDraft(profile);
    if (profile.version !== PROFILE_VERSION || profile.birth_date === undefined || profile.birth_date === null || profile.birth_date === '') {
      throw new KnowFitValidationError('BIRTH_DATE_REQUIRED', 'Indica tu fecha de nacimiento para actualizar el plan.', { field: 'birth_date' });
    }
    const ageYears = requirePositiveInteger(calculateAge(profile.birth_date, referenceDate), 'age_years');

    const goal = requireEnum(profile.goal, GOAL_FACTORS, 'goal');
    const sex = requireEnum(profile.sex, GEB_COEFFICIENTS, 'sex');
    const activityLevel = requireEnum(
      profile.activity_level,
      ACTIVITY_FACTORS,
      'activity_level'
    );
    const coverageMode = requireEnum(
      profile.coverage_mode,
      COVERAGE_FACTORS,
      'coverage_mode'
    );

    const daysPerWeek = requireDays(profile.days_per_week);
    const training = normalizeWeeklyTraining(profile);

    return Object.freeze({
      version: PROFILE_VERSION,
      goal,
      sex,
      birth_date: profile.birth_date,
      age_years: ageYears,
      age_calculated_on: referenceDate,
      weight: requirePositiveNumber(profile.weight, 'weight'),
      height: requirePositiveInteger(profile.height, 'height'),
      activity_level: activityLevel,
      days_per_week: daysPerWeek,
      coverage_mode: coverageMode,
      training_model: TRAINING_MODEL,
      training_collected: true,
      does_sport: profile.does_sport,
      ...training,
      eats_fish: profile.eats_fish !== false && profile.eats_fish !== 'false',
    });
  }

  function calculateGeb(profile) {
    const coefficients = GEB_COEFFICIENTS[profile.sex];

    return round(
      coefficients.base
        + coefficients.weight * profile.weight
        + coefficients.height * profile.height
        - coefficients.age * profile.age_years
    );
  }

  function calculateProfile(inputProfile, options = {}) {
    const profile = normalizeProfile(inputProfile, options);
    const goalFactor = GOAL_FACTORS[profile.goal];
    const proteinFactor = PROTEIN_FACTORS[profile.goal];
    const activityFactor = ACTIVITY_FACTORS[profile.activity_level];
    const coverageFactor = COVERAGE_FACTORS[profile.coverage_mode];

    const gebKcal = calculateGeb(profile);
    const getKcal = round(gebKcal * activityFactor);

    const trainingMet = profile.does_sport ? TRAINING_CATEGORIES[profile.training_category].met : 0;
    // Net exercise energy is averaged over seven calendar days, independently
    // of purchased menu days. NO must return zero without evaluating MET - 1.
    const trainingWeeklyExtraKcal = profile.does_sport
      ? round((trainingMet - 1) * profile.weight * profile.training_sessions_per_week * TRAINING_SESSION_HOURS)
      : 0;
    const trainingExtraKcal = round(trainingWeeklyExtraKcal / 7);
    const trainingSummary = Object.freeze({
      model: TRAINING_MODEL,
      does_sport: profile.does_sport,
      category: profile.training_category,
      sessions_per_week: profile.training_sessions_per_week,
      met: trainingMet,
      session_hours: TRAINING_SESSION_HOURS,
    });

    const adjustedDailyKcal = round((getKcal + trainingExtraKcal) * goalFactor);
    const knowfitTargetKcal = round(adjustedDailyKcal * coverageFactor);
    const dailyProteinG = round(profile.weight * proteinFactor);

    // TECH applies the contracted coverage once to both kcal and protein.
    const knowfitTargetProteinG = round(dailyProteinG * coverageFactor);

    if (![gebKcal, getKcal, trainingWeeklyExtraKcal, trainingExtraKcal, adjustedDailyKcal,
      knowfitTargetKcal, dailyProteinG, knowfitTargetProteinG].every(Number.isFinite)) {
      throw new KnowFitValidationError('INVALID_CALCULATION', 'Los datos del perfil no permiten calcular un objetivo válido.');
    }

    const dailyTargets = Array.from({ length: profile.days_per_week }, (_, index) => Object.freeze({
      day: index + 1,
      training_extra_kcal: trainingExtraKcal,
      target_kcal: knowfitTargetKcal,
      target_protein_g: knowfitTargetProteinG,
      training_summary: trainingSummary,
    }));

    return Object.freeze({
      version: ENGINE_VERSION,
      profile,
      factors: Object.freeze({
        goal: goalFactor,
        protein_g_per_kg: proteinFactor,
        activity: activityFactor,
        coverage: coverageFactor,
      }),
      calculation: Object.freeze({
        geb_kcal: gebKcal,
        get_kcal: getKcal,
        training_extra_kcal: trainingExtraKcal,
        training_weekly_extra_kcal: trainingWeeklyExtraKcal,
        training_summary: trainingSummary,
        adjusted_daily_kcal: adjustedDailyKcal,
        daily_protein_g: dailyProteinG,
        knowfit_target_kcal: knowfitTargetKcal,
        knowfit_target_protein_g: knowfitTargetProteinG,
      }),
      daily_targets: Object.freeze(dailyTargets),
      assumptions: Object.freeze({
        training_included: profile.does_sport,
        training_calculation_enabled: TRAINING_CALCULATION_ENABLED,
        protein_scaled_by_coverage: true,
        basal_formula: 'mifflin_st_jeor',
        training_model: TRAINING_MODEL,
      }),
    });
  }

  function parseNutritionNumber(value, label) {
    const normalizedLabel = String(label || '').toUpperCase();
    const stringValue = String(value ?? '').trim();

    // Product content may use Spanish thousands formatting such as 1.022 KCAL.
    // Decimal macro values still use a comma or a regular decimal point.
    if (normalizedLabel === 'KCAL' && /^\d{1,3}(?:\.\d{3})+$/.test(stringValue)) {
      return parseNumericValue(stringValue.replace(/\./g, ''));
    }

    return parseNumericValue(stringValue);
  }

  function parseNutritionEntries(rawNutrition) {
    let entries = rawNutrition;

    if (typeof entries === 'string') {
      try {
        entries = JSON.parse(entries);
      } catch (_error) {
        entries = [entries];
      }
    }

    if (!Array.isArray(entries)) {
      return Object.freeze({ kcal: null, protein_g: null, carbs_g: null, fat_g: null });
    }

    const nutrition = {
      kcal: null,
      protein_g: null,
      carbs_g: null,
      fat_g: null,
    };

    entries.forEach((entry) => {
      if (typeof entry !== 'string') return;
      const match = entry.trim().match(/^([+-]?\d[\d.,]*)\s*([a-z]+)$/i);
      if (!match) return;

      const label = match[2].toUpperCase();
      const value = parseNutritionNumber(match[1], label);
      if (value === null || value < 0) return;

      if (label === 'KCAL') nutrition.kcal = value;
      if (label === 'P') nutrition.protein_g = value;
      if (label === 'CH') nutrition.carbs_g = value;
      if (label === 'G') nutrition.fat_g = value;
    });

    return Object.freeze(nutrition);
  }

  // Optional nutrients are grams per served product. Missing/invalid values
  // remain unknown; treating them as zero would reward incomplete catalog data.
  function parseNutrientGrams(value) {
    if (typeof value === 'string') value = value.trim().replace(/\s*g\s*$/i, '');
    const grams = parseNumericValue(value);
    return grams !== null && grams >= 0 ? round(grams) : null;
  }

  function parseNutritionTable(rawTable, weightG) {
    let table = rawTable;
    if (typeof table === 'string') {
      try { table = JSON.parse(table); } catch (_error) { /* Plain text/HTML is supported as well. */ }
    }
    const rows = [];
    const collectText = (node) => typeof node === 'string' ? node
      : node && typeof node === 'object' ? String(node.value || '')
        + (Array.isArray(node.children) ? node.children.map(collectText).join('') : '') : '';
    const visit = (node) => {
      if (!node) return;
      if (typeof node === 'string') {
        rows.push(...node.replace(/<\/(?:li|p|tr)>/gi, '\n').replace(/<[^>]+>/g, '').split(/\r?\n/));
      } else if (Array.isArray(node)) node.forEach(visit);
      else if (node.type === 'list-item' || node.type === 'paragraph') rows.push(collectText(node));
      else if (Array.isArray(node.children)) node.children.forEach(visit);
    };
    visit(table);
    const result = Object.fromEntries(['fat_g', 'saturated_fat_g', 'sugar_g', 'salt_g'].map((field) => [field, null]));
    let headers = null;
    rows.forEach((row) => {
      const cells = row.split('|').map((cell) => cell.trim());
      const normalizedCells = cells.map((cell) => cell.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
      if (normalizedCells.some((cell) => /por\s*\d+(?:[.,]\d+)?\s*g\b|por\s+(?:porcion|plato|racion)/.test(cell))) {
        headers = normalizedCells;
        return;
      }
      if (cells.length < 2) return;
      if (!headers) return; // Never guess that an unlabelled column is per portion.
      const label = normalizedCells[0];
      const field = /\bsaturad[oa]s?\b/.test(label) ? 'saturated_fat_g'
        : /^(?:de los cuales[,:]?\s*)?azucar(?:es)?(?:\s+total(?:es)?)?$/.test(label) ? 'sugar_g'
          : /^sal(?:\s+total)?$/.test(label) ? 'salt_g'
            : /^(?:grasas?|lipidos?)(?:\s+totales?)?$/.test(label) ? 'fat_g' : null;
      if (!field) return;
      // Some tables omit the header above nutrient names. Determine that from
      // the header, never from row length: a missing portion cell must not
      // shift the per-100g value into the portion column.
      const offset = /^por\s*(?:\d|porcion|plato|racion)/.test(headers[0]) ? 1 : 0;
      const portionIndex = headers.findIndex((header) => {
        if (/por\s+(?:porcion|plato|racion)/.test(header)) return true;
        const weightMatch = header.match(/por\s*(\d+(?:[.,]\d+)?)\s*g\b/);
        if (!weightMatch) return false;
        const headerWeight = parseNumericValue(weightMatch[1]);
        return headerWeight !== 100 && (!(Number.isFinite(weightG) && weightG > 0) || headerWeight === weightG);
      });
      if (portionIndex >= 0) {
        const value = parseNutrientGrams(cells[portionIndex + offset]);
        if (value !== null) { result[field] = value; return; }
      }
      const per100Index = headers.findIndex((header) => /por\s*100(?:[.,]0+)?\s*g\b/.test(header));
      const value = per100Index >= 0 ? parseNutrientGrams(cells[per100Index + offset]) : null;
      if (value !== null && Number.isFinite(weightG) && weightG > 0) result[field] = round(value * weightG / 100);
    });
    return Object.freeze(result);
  }

  const NUTRIENTS_PER_100G = Object.freeze([
    'kcal', 'protein_g', 'fat_g', 'saturated_fat_g', 'carbs_g', 'sugar_g', 'fiber_g', 'salt_g',
  ]);
  const OPTIONAL_NUTRIENTS = Object.freeze(NUTRIENTS_PER_100G.filter((field) =>
    field !== 'kcal' && field !== 'protein_g'
  ));
  const NUTRITION_QUALITY_LIMITS = Object.freeze({ fat_kcal_ratio: 0.35, saturated_fat_ratio: 0.5 });

  function summarizeNutrition(items) {
    const totals = { kcal: 0, protein_g: 0, price_cents: 0 };
    const complete = Object.fromEntries(OPTIONAL_NUTRIENTS.map((field) => [field, true]));
    OPTIONAL_NUTRIENTS.forEach((field) => { totals[field] = 0; });
    (Array.isArray(items) ? items : []).forEach((item) => {
      const rawQuantity = parseNumericValue(item.quantity);
      const quantity = rawQuantity !== null && rawQuantity > 0 ? rawQuantity : 1;
      totals.kcal += Number(item.kcal || 0) * quantity;
      totals.protein_g += Number(item.protein_g || 0) * quantity;
      totals.price_cents += Number(item.price_cents || 0) * quantity;
      OPTIONAL_NUTRIENTS.forEach((field) => {
        const grams = parseNutrientGrams(item[field]);
        if (grams === null) complete[field] = false;
        else totals[field] += grams * quantity;
      });
    });
    OPTIONAL_NUTRIENTS.forEach((field) => { totals[field] = complete[field] ? round(totals[field]) : null; });
    totals.kcal = round(totals.kcal);
    totals.protein_g = round(totals.protein_g);
    totals.price_cents = Math.round(totals.price_cents);
    return Object.freeze(totals);
  }

  function evaluateNutritionQuality(items) {
    return evaluateNutritionTotals(summarizeNutrition(items));
  }

  function evaluateNutritionTotals(totals) {
    const fatRatio = totals.fat_g !== null && totals.kcal > 0 ? totals.fat_g * 9 / totals.kcal : null;
    const saturatedRatio = totals.fat_g === null || totals.saturated_fat_g === null
      || totals.saturated_fat_g > totals.fat_g ? null
      // With both measured totals zero, the bound saturated <= 0.5 * fat
      // holds. Use zero as the display ratio without treating missing as zero.
      : totals.fat_g === 0 ? 0 : totals.saturated_fat_g / totals.fat_g;
    const assessment = (ratio, limit) => Object.freeze({
      ratio,
      limit_ratio: limit,
      status: ratio === null ? 'unknown' : ratio > limit + 1e-12 ? 'above' : 'within',
    });
    return Object.freeze({
      totals,
      fat: assessment(fatRatio, NUTRITION_QUALITY_LIMITS.fat_kcal_ratio),
      saturated_fat: assessment(saturatedRatio, NUTRITION_QUALITY_LIMITS.saturated_fat_ratio),
    });
  }

  function deviationBand(absoluteRatio) {
    return absoluteRatio <= 0.05 + 1e-12 ? 0
      : absoluteRatio <= 0.10 + 1e-12 ? 1
        : absoluteRatio < 0.15 - 1e-12 ? 2 : 3;
  }

  function getDeviationStatus(actual, target) {
    const actualValue = parseNumericValue(actual);
    const targetValue = parseNumericValue(target);
    if (actualValue === null || actualValue < 0 || targetValue === null || targetValue <= 0) {
      return Object.freeze({ severity: 'unknown', deviation_ratio: null });
    }
    const ratio = (actualValue - targetValue) / targetValue;
    const absoluteRatio = Math.abs(ratio);
    const severity = ['green', 'yellow', 'orange', 'red'][deviationBand(absoluteRatio)];
    return Object.freeze({ severity, deviation_ratio: ratio });
  }

  function normalizeId(value) {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
  }

  function normalizeCollectionIds(value) {
    if (!Array.isArray(value)) return [];
    return [...new Set(value.map(normalizeId).filter(Boolean))].sort();
  }

  function normalizeBadges(value) {
    if (!Array.isArray(value)) return [];
    const badges = new Map();

    value.forEach((rawBadge) => {
      const label = String(
        typeof rawBadge === 'string' ? rawBadge : rawBadge?.label ?? ''
      ).trim();
      if (!label) return;

      const rawKind = typeof rawBadge === 'string' ? rawBadge : rawBadge?.kind ?? label;
      const kind = String(rawKind)
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      if (!kind) return;

      badges.set(`${kind}:${label.toLowerCase()}`, { kind, label });
    });

    return [...badges.values()];
  }

  function normalizeFoodIndicators(value) {
    if (!Array.isArray(value)) return [];
    const indicators = new Map();

    value.forEach((rawIndicator) => {
      if (!rawIndicator || typeof rawIndicator !== 'object') return;
      const url = String(rawIndicator.url ?? rawIndicator.image ?? '').trim();
      if (!url) return;
      const label = String(rawIndicator.label ?? rawIndicator.alt ?? '').trim();
      indicators.set(url, { url, label });
    });

    return [...indicators.values()];
  }

  function normalizePositiveInteger(value) {
    const numericValue = parseNumericValue(value);
    return numericValue !== null && numericValue > 0 ? Math.round(numericValue) : null;
  }

  function normalizeNonNegativeMoney(value) {
    const numericValue = parseNumericValue(value);
    return numericValue !== null && numericValue >= 0 ? Math.round(numericValue) : 0;
  }

  function parsePortionWeight(value) {
    let grams = value;
    if (typeof value === 'string') {
      const match = value.trim().match(/^(\d+(?:[.,]\d+)?|[.,]\d+)\s*(kg|g)?$/i);
      if (!match) return null;
      grams = Number(match[1].replace(',', '.')) * (match[2]?.toLowerCase() === 'kg' ? 1000 : 1);
    }
    if (typeof grams !== 'number' || !Number.isFinite(grams) || grams <= 0) return null;
    const rounded = round(grams);
    return Number.isFinite(rounded) && rounded > 0 ? rounded : null;
  }

  // Portion variants compete as alternatives of the same dish. Product keys
  // also identify uses in the weekly repetition map supplied to evaluations.
  function getProductKey(product) {
    return String(product?.product_id || product?.variant_id || '');
  }

  function normalizeMealTypes(rawProduct) {
    const rawMealTypes = Array.isArray(rawProduct.meal_types ?? rawProduct.mealTypes)
      ? (rawProduct.meal_types ?? rawProduct.mealTypes)
      : [rawProduct.meal_type ?? rawProduct.mealType ?? 'main'];
    const mealTypes = [...new Set(
      rawMealTypes.map((value) => String(value).trim().toLowerCase()).filter(Boolean)
    )];

    const unsupported = mealTypes.find((mealType) =>
      !Object.prototype.hasOwnProperty.call(SUPPORTED_MEAL_TYPES, mealType)
    );
    if (unsupported) {
      throw new KnowFitSelectionError(
        'UNSUPPORTED_MEAL_TYPE',
        'El tipo de comida del producto no está soportado.',
        { meal_type: unsupported }
      );
    }

    return mealTypes.length > 0 ? mealTypes.sort() : ['main'];
  }

  function resolveCatalogSources(rawCatalog) {
    if (!rawCatalog || Array.isArray(rawCatalog) || !rawCatalog.sources
      || typeof rawCatalog.sources !== 'object' || Array.isArray(rawCatalog.sources)) return rawCatalog;

    const pairs = { breakfast: 'snack', main: 'dinner', snack: 'breakfast', dinner: 'main' };
    const isTrue = (value) => value === true || value === 'true';
    const sourceTypes = [...Object.keys(pairs), ...('extra' in rawCatalog.sources ? ['extra'] : [])];
    const sources = Object.fromEntries(sourceTypes.map((mealType) => {
      const source = rawCatalog.sources[mealType] || {};
      const assigned = Object.prototype.hasOwnProperty.call(source, 'assigned')
        ? isTrue(source.assigned) : isTrue(source.configured);
      const configured = assigned && (Object.prototype.hasOwnProperty.call(source, 'configured')
        ? isTrue(source.configured) : true);
      return [mealType, {
        ...source, assigned, configured, fallback_from: null,
        // Discard any effective source copied by an earlier resolution.
        id: assigned ? source.id ?? null : null,
        handle: assigned ? source.handle ?? '' : '',
        title: assigned ? source.title ?? '' : '',
        product_count: assigned ? source.product_count ?? 0 : 0,
      }];
    }));

    Object.entries(pairs).forEach(([mealType, partner]) => {
      if (sources[mealType].assigned || !sources[partner].assigned || !sources[partner].configured) return;
      sources[mealType] = { ...sources[partner], assigned: false, fallback_from: partner };
    });

    const hasFallback = Object.values(sources).some((source) => source.fallback_from);
    const products = Array.isArray(rawCatalog.products) ? rawCatalog.products.map((product) => {
      if (!product || typeof product !== 'object' || Array.isArray(product)) return product;
      if (!hasFallback && !Array.isArray(product.assigned_meal_types)) return product;
      const assignedTypes = normalizeAssignedMealTypes(product);
      const effectiveTypes = new Set(assignedTypes);
      Object.entries(sources).forEach(([mealType, source]) => {
        if (source.fallback_from && assignedTypes.includes(source.fallback_from)) effectiveTypes.add(mealType);
      });
      const mealTypes = [...effectiveTypes].sort();
      return { ...product, assigned_meal_types: assignedTypes, meal_type: mealTypes[0], meal_types: mealTypes };
    }) : rawCatalog.products;
    return { ...rawCatalog, sources, products };
  }

  function normalizeAssignedMealTypes(product) {
    const types = Array.isArray(product.assigned_meal_types) ? product.assigned_meal_types
      : Array.isArray(product.meal_types ?? product.mealTypes) ? product.meal_types ?? product.mealTypes
      : [product.meal_type ?? product.mealType ?? 'main'];
    const normalized = [...new Set(types.map((value) => String(value).trim().toLowerCase()).filter(Boolean))].sort();
    return normalized.length ? normalized : ['main'];
  }

  function normalizeCatalogProduct(rawProduct, index, options = {}) {
    if (!rawProduct || typeof rawProduct !== 'object' || Array.isArray(rawProduct)) {
      throw new KnowFitSelectionError(
        'INVALID_PRODUCT',
        'El producto del catálogo debe ser un objeto.',
        { index }
      );
    }

    const productId = normalizeId(rawProduct.product_id ?? rawProduct.productId);
    const variantId = normalizeId(rawProduct.variant_id ?? rawProduct.variantId);
    if (!productId || !variantId) {
      throw new KnowFitSelectionError(
        'MISSING_PRODUCT_ID',
        'El producto necesita product_id y variant_id.',
        { index, product_id: productId, variant_id: variantId }
      );
    }

    const available = rawProduct.available !== false && rawProduct.available !== 'false';
    const published = rawProduct.published !== false && rawProduct.published !== 'false';
    if (!published || (!available && options.include_unavailable !== true)) {
      throw new KnowFitSelectionError(
        'PRODUCT_UNAVAILABLE',
        'El producto no está publicado o su variante no está disponible.',
        { index, product_id: productId, variant_id: variantId }
      );
    }

    let mealTypes;
    try {
      mealTypes = normalizeMealTypes(rawProduct);
    } catch (error) {
      if (error instanceof KnowFitSelectionError) {
        error.details = { index, product_id: productId, ...error.details };
      }
      throw error;
    }

    const hasPer100g = Object.prototype.hasOwnProperty.call(rawProduct, 'nutrition_per_100g');
    let weightG;
    let nutritionPer100g;
    let nutrition;
    if (hasPer100g) {
      // Accept one explicit portion size only, never guess from compound text.
      weightG = parsePortionWeight(rawProduct.weight_g);
      if (weightG === null) {
        throw new KnowFitSelectionError(
          'INVALID_VARIANT_WEIGHT',
          'La nutrición por 100 g necesita un peso numérico positivo de la variante.',
          { index, product_id: productId, variant_id: variantId, weight_g: rawProduct.weight_g }
        );
      }
      const source = rawProduct.nutrition_per_100g;
      if (!source || typeof source !== 'object' || Array.isArray(source)) {
        throw new KnowFitSelectionError(
          'INVALID_PRODUCT_NUTRITION',
          'La nutrición por 100 g debe ser un objeto de nutrientes.',
          { index, product_id: productId, variant_id: variantId }
        );
      }
      nutritionPer100g = Object.freeze(Object.fromEntries(NUTRIENTS_PER_100G.map((field) => {
        const value = parseNumericValue(source[field]);
        return [field, value !== null && value >= 0 ? value : null];
      })));
      // Preserve the original per-100g values so snapshots can be normalized
      // again without scaling the already converted portion a second time.
      nutrition = Object.fromEntries(NUTRIENTS_PER_100G.map((field) => [
        field, nutritionPer100g[field] === null ? null : round(nutritionPer100g[field] * weightG / 100),
      ]));
    } else {
      const parsedNutrition = parseNutritionEntries(
        rawProduct.nutrition ?? rawProduct.nutrition_entries ?? rawProduct.nutritionEntries
      );
      weightG = parseNumericValue(
        rawProduct.weight_g ?? rawProduct.weightG ?? rawProduct.gramaje_g ?? rawProduct.gramaje
      );
      const tableNutrition = parseNutritionTable(rawProduct.nutrition_table ?? rawProduct.nutritionTable, weightG);
      nutrition = {
        kcal: parseNumericValue(rawProduct.kcal ?? rawProduct.knowfit_kcal) ?? parsedNutrition.kcal,
        protein_g: parseNumericValue(rawProduct.protein_g ?? rawProduct.proteinG ?? rawProduct.knowfit_protein_g) ?? parsedNutrition.protein_g,
        fat_g: parseNutrientGrams(rawProduct.fat_g ?? rawProduct.fatG ?? rawProduct.knowfit_fat_g) ?? parsedNutrition.fat_g ?? tableNutrition.fat_g,
        carbs_g: parseNumericValue(rawProduct.carbs_g ?? rawProduct.carbsG ?? rawProduct.knowfit_carbs_g) ?? parsedNutrition.carbs_g,
        saturated_fat_g: parseNutrientGrams(rawProduct.saturated_fat_g ?? rawProduct.saturatedFatG) ?? tableNutrition.saturated_fat_g,
        sugar_g: parseNutrientGrams(rawProduct.sugar_g ?? rawProduct.sugarG) ?? tableNutrition.sugar_g,
        fiber_g: parseNutrientGrams(rawProduct.fiber_g ?? rawProduct.fiberG),
        salt_g: parseNutrientGrams(rawProduct.salt_g ?? rawProduct.saltG) ?? tableNutrition.salt_g,
      };
    }
    const kcal = nutrition.kcal;
    const proteinG = nutrition.protein_g;
    if (!Number.isFinite(kcal) || kcal <= 0 || !Number.isFinite(proteinG) || proteinG < 0) {
      throw new KnowFitSelectionError(
        'INVALID_PRODUCT_NUTRITION',
        'El producto necesita calorías y proteína numéricas válidas.',
        { index, product_id: productId, variant_id: variantId, kcal, protein_g: proteinG }
      );
    }

    const rawPrice = rawProduct.price_cents ?? rawProduct.priceCents ?? 0;
    const priceCents = parseNumericValue(rawPrice);
    if (priceCents === null || priceCents < 0) {
      throw new KnowFitSelectionError(
        'INVALID_PRODUCT_PRICE',
        'El precio del producto debe expresarse en céntimos y no puede ser negativo.',
        { index, product_id: productId, price_cents: rawPrice }
      );
    }

    const containsFish = rawProduct.contains_fish === true
      || rawProduct.containsFish === true
      || rawProduct.contains_fish === 'true'
      || rawProduct.containsFish === 'true';

    return {
      product_id: productId,
      variant_id: variantId,
      collection_ids: normalizeCollectionIds(
        rawProduct.collection_ids ?? rawProduct.collectionIds
      ),
      title: String(rawProduct.title ?? ''),
      product_title: String(rawProduct.product_title ?? rawProduct.title ?? ''),
      variant_title: String(rawProduct.variant_title ?? ''),
      handle: String(rawProduct.handle ?? ''),
      url: String(rawProduct.url ?? rawProduct.product_url ?? ''),
      image: String(rawProduct.image ?? ''),
      image_alt: String(rawProduct.image_alt ?? rawProduct.imageAlt ?? rawProduct.title ?? ''),
      image_width: normalizePositiveInteger(rawProduct.image_width ?? rawProduct.imageWidth),
      image_height: normalizePositiveInteger(rawProduct.image_height ?? rawProduct.imageHeight),
      secondary_image: String(rawProduct.secondary_image ?? rawProduct.secondaryImage ?? ''),
      secondary_image_alt: String(
        rawProduct.secondary_image_alt ?? rawProduct.secondaryImageAlt ?? rawProduct.title ?? ''
      ),
      secondary_image_width: normalizePositiveInteger(
        rawProduct.secondary_image_width ?? rawProduct.secondaryImageWidth
      ),
      secondary_image_height: normalizePositiveInteger(
        rawProduct.secondary_image_height ?? rawProduct.secondaryImageHeight
      ),
      price_cents: Math.round(priceCents),
      compare_at_price_cents: normalizeNonNegativeMoney(
        rawProduct.compare_at_price_cents ?? rawProduct.compareAtPriceCents
      ),
      available,
      published: true,
      meal_type: mealTypes[0],
      meal_types: mealTypes,
      ...(Array.isArray(rawProduct.assigned_meal_types)
        ? { assigned_meal_types: normalizeAssignedMealTypes(rawProduct) } : {}),
      contains_fish: containsFish,
      is_premium: rawProduct.is_premium === true || rawProduct.isPremium === true
        || rawProduct.is_premium === 'true' || rawProduct.isPremium === 'true',
      surcharge_cents: normalizeNonNegativeMoney(
        rawProduct.surcharge_cents ?? rawProduct.surchargeCents
      ),
      badges: normalizeBadges(rawProduct.badges),
      food_indicators: normalizeFoodIndicators(
        rawProduct.food_indicators ?? rawProduct.foodIndicators
      ),
      kcal: round(kcal),
      protein_g: round(proteinG),
      weight_g: weightG !== null && weightG > 0 ? weightG : null,
      variant_weight_source: String(rawProduct.variant_weight_source ?? ''),
      nutrition_source: hasPer100g ? 'per_100g' : 'legacy_per_portion',
      ...(hasPer100g ? { nutrition_per_100g: nutritionPer100g } : {}),
      ...Object.fromEntries(OPTIONAL_NUTRIENTS.map((field) => [
        field, Number.isFinite(nutrition[field]) && nutrition[field] >= 0 ? round(nutrition[field]) : null,
      ])),
    };
  }

  function freezeCatalogProduct(product) {
    return Object.freeze({
      ...product,
      collection_ids: Object.freeze([...product.collection_ids]),
      meal_types: Object.freeze([...product.meal_types]),
      ...(product.assigned_meal_types ? { assigned_meal_types: Object.freeze([...product.assigned_meal_types]) } : {}),
      badges: Object.freeze(product.badges.map((badge) => Object.freeze({ ...badge }))),
      food_indicators: Object.freeze(
        product.food_indicators.map((indicator) => Object.freeze({ ...indicator }))
      ),
    });
  }

  function mergeObjectsByKey(firstItems, secondItems, keyForItem) {
    return [...new Map(
      [...firstItems, ...secondItems].map((item) => [keyForItem(item), item])
    ).values()];
  }

  function mergeDuplicateProduct(existing, duplicate) {
    const per100gSource = Object.prototype.hasOwnProperty.call(existing, 'nutrition_per_100g') ? existing
      : Object.prototype.hasOwnProperty.call(duplicate, 'nutrition_per_100g') ? duplicate : null;
    const mealTypes = [...new Set([...existing.meal_types, ...duplicate.meal_types])].sort();
    return {
      ...existing,
      product_title: existing.product_title || duplicate.product_title,
      variant_title: existing.variant_title || duplicate.variant_title,
      variant_weight_source: existing.variant_weight_source || duplicate.variant_weight_source,
      available: existing.available || duplicate.available,
      collection_ids: [...new Set([
        ...existing.collection_ids,
        ...duplicate.collection_ids,
      ])].sort(),
      meal_type: mealTypes[0],
      meal_types: mealTypes,
      ...(existing.assigned_meal_types || duplicate.assigned_meal_types ? {
        assigned_meal_types: [...new Set([
          ...normalizeAssignedMealTypes(existing), ...normalizeAssignedMealTypes(duplicate),
        ])].sort(),
      } : {}),
      contains_fish: existing.contains_fish || duplicate.contains_fish,
      url: existing.url || duplicate.url,
      image: existing.image || duplicate.image,
      image_alt: existing.image_alt || duplicate.image_alt,
      image_width: existing.image_width || duplicate.image_width,
      image_height: existing.image_height || duplicate.image_height,
      secondary_image: existing.secondary_image || duplicate.secondary_image,
      secondary_image_alt: existing.secondary_image_alt || duplicate.secondary_image_alt,
      secondary_image_width: existing.secondary_image_width || duplicate.secondary_image_width,
      secondary_image_height: existing.secondary_image_height || duplicate.secondary_image_height,
      compare_at_price_cents: Math.max(
        existing.compare_at_price_cents,
        duplicate.compare_at_price_cents
      ),
      is_premium: existing.is_premium || duplicate.is_premium,
      surcharge_cents: Math.max(existing.surcharge_cents, duplicate.surcharge_cents),
      weight_g: existing.weight_g ?? duplicate.weight_g,
      carbs_g: existing.carbs_g ?? duplicate.carbs_g,
      fat_g: existing.fat_g ?? duplicate.fat_g,
      saturated_fat_g: existing.saturated_fat_g ?? duplicate.saturated_fat_g,
      sugar_g: existing.sugar_g ?? duplicate.sugar_g,
      fiber_g: existing.fiber_g ?? duplicate.fiber_g,
      salt_g: existing.salt_g ?? duplicate.salt_g,
      // An authoritative new source wins as a whole. Do not fill its unknown
      // nutrients from a stale legacy copy emitted by another collection.
      ...(per100gSource ? Object.fromEntries([
        'weight_g', 'variant_weight_source', 'nutrition_source', 'nutrition_per_100g', ...NUTRIENTS_PER_100G,
      ].map((field) => [field, per100gSource[field]])) : {}),
      badges: mergeObjectsByKey(
        existing.badges,
        duplicate.badges,
        (badge) => `${badge.kind}:${badge.label.toLowerCase()}`
      ),
      food_indicators: mergeObjectsByKey(
        existing.food_indicators,
        duplicate.food_indicators,
        (indicator) => indicator.url
      ),
    };
  }

  function normalizeCatalog(rawCatalog, options = {}) {
    rawCatalog = resolveCatalogSources(rawCatalog);
    const rawProducts = Array.isArray(rawCatalog) ? rawCatalog : rawCatalog?.products;
    if (!Array.isArray(rawProducts)) {
      throw new KnowFitSelectionError(
        'INVALID_CATALOG',
        'El catálogo KnowFit debe incluir un array de productos.'
      );
    }

    const rawSources = !Array.isArray(rawCatalog) && rawCatalog?.sources
      && typeof rawCatalog.sources === 'object'
      ? rawCatalog.sources
      : {};
    const sources = Object.freeze(
      Object.fromEntries(['breakfast', 'main', 'snack', 'dinner', 'extra'].map((mealType) => {
        const source = rawSources[mealType] || {};
        return [mealType, Object.freeze({
          configured: source.configured === true || source.configured === 'true',
          assigned: source.assigned === true || source.assigned === 'true',
          fallback_from: source.fallback_from || null,
          id: normalizeId(source.id),
          handle: String(source.handle ?? ''),
          title: String(source.title ?? ''),
          product_count: Math.max(0, Number(source.product_count) || 0),
        })];
      }))
    );

    const productsByVariant = new Map();
    const invalidPer100gVariants = new Set();
    const rejected = [];
    let duplicatesMerged = 0;

    rawProducts.forEach((rawProduct, index) => {
      try {
        const product = normalizeCatalogProduct(rawProduct, index, options);
        const existing = productsByVariant.get(product.variant_id);

        if (existing) {
          productsByVariant.set(
            product.variant_id,
            mergeDuplicateProduct(existing, product)
          );
          duplicatesMerged += 1;
          return;
        }

        productsByVariant.set(product.variant_id, product);
      } catch (error) {
        if (!(error instanceof KnowFitSelectionError)) throw error;
        if (rawProduct && Object.prototype.hasOwnProperty.call(rawProduct, 'nutrition_per_100g')
          && ['INVALID_PRODUCT_NUTRITION', 'INVALID_VARIANT_WEIGHT'].includes(error.code)) {
          invalidPer100gVariants.add(normalizeId(rawProduct.variant_id ?? rawProduct.variantId));
        }
        rejected.push(Object.freeze({
          index,
          code: error.code,
          message: error.message,
          details: Object.freeze({ ...error.details }),
        }));
      }
    });

    // A stale legacy copy from another collection must not silently rescue a
    // variant whose authoritative nutrition or portion size is incomplete.
    invalidPer100gVariants.forEach((variantId) => productsByVariant.delete(variantId));
    const products = [...productsByVariant.values()]
      .sort((first, second) => first.variant_id.localeCompare(second.variant_id))
      .map(freezeCatalogProduct);

    return Object.freeze({
      input_count: rawProducts.length,
      products: Object.freeze(products),
      rejected: Object.freeze(rejected),
      duplicates_merged: duplicatesMerged,
      sources,
    });
  }

  function mergeCatalogSnapshots(rawCatalogs) {
    const snapshots = Array.isArray(rawCatalogs) ? rawCatalogs : [rawCatalogs];
    const sources = {};
    const products = [];

    snapshots.forEach((snapshot) => {
      if (!snapshot) return;
      if (Array.isArray(snapshot)) {
        products.push(...snapshot);
        return;
      }
      if (Array.isArray(snapshot.products)) products.push(...snapshot.products);
      if (!snapshot.sources || typeof snapshot.sources !== 'object') return;
      Object.entries(snapshot.sources).forEach(([mealType, source]) => {
        const existing = sources[mealType];
        const isAssigned = (value) => Object.prototype.hasOwnProperty.call(value || {}, 'assigned')
          ? value.assigned === true || value.assigned === 'true'
          : value?.configured === true || value?.configured === 'true';
        if (!existing || isAssigned(source)
          || (!isAssigned(existing) && (source?.configured === true || source?.configured === 'true'))) {
          sources[mealType] = { ...(source || {}) };
        }
      });
    });

    return { sources, products };
  }

  function getCatalogSelectionSignature(rawCatalog) {
    const catalog = normalizeCatalog(rawCatalog, { include_unavailable: true });
    const signaturePayload = {
      sources: Object.entries(catalog.sources).map(([mealType, source]) => ({
        meal_type: mealType,
        configured: source.configured,
        id: source.id,
      })),
      products: catalog.products.map((product) => ({
        product_id: product.product_id,
        variant_id: product.variant_id,
        collection_ids: product.collection_ids,
        meal_types: product.meal_types,
        available: product.available,
        weight_g: product.weight_g,
        nutrition_source: product.nutrition_source,
        nutrition_per_100g: product.nutrition_per_100g,
        kcal: product.kcal,
        protein_g: product.protein_g,
        fat_g: product.fat_g,
        saturated_fat_g: product.saturated_fat_g,
        carbs_g: product.carbs_g,
        sugar_g: product.sugar_g,
        fiber_g: product.fiber_g,
        salt_g: product.salt_g,
        contains_fish: product.contains_fish,
      })),
    };
    const serialized = JSON.stringify(signaturePayload);
    let hash = 2166136261;
    for (let index = 0; index < serialized.length; index += 1) {
      hash ^= serialized.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return `v1-${(hash >>> 0).toString(16).padStart(8, '0')}`;
  }

  function getCatalogRegenerationReason({
    stored_signature: storedSignature = '',
    current_signature: currentSignature = '',
    invalid_selection: invalidSelection = false,
  } = {}) {
    const stored = String(storedSignature || '');
    const current = String(currentSignature || '');
    if (current && stored !== current) return 'catalog_signature_changed';
    if (invalidSelection) return 'selected_product_unavailable';
    return '';
  }

  function normalizeSelectionOptions(options = {}) {
    // Ignore retired 65/35 weights in previously saved plans.
    const normalized = Object.fromEntries(Object.entries(SELECTION_DEFAULTS).map(([field, defaultValue]) => [
      field, options[field] ?? defaultValue,
    ]));

    ['meal_distribution_weight', 'repeat_penalty_per_previous_use'].forEach((field) => {
      const value = Number(normalized[field]);
      if (!Number.isFinite(value) || value < 0) {
        throw new KnowFitSelectionError(
          'INVALID_SELECTION_OPTION',
          `${field} debe ser un número igual o mayor que cero.`,
          { field, value: normalized[field] }
        );
      }
      normalized[field] = value;
    });

    const alternatives = Number(normalized.alternatives_per_day);
    if (!Number.isInteger(alternatives) || alternatives < 0) {
      throw new KnowFitSelectionError(
        'INVALID_SELECTION_OPTION',
        'alternatives_per_day debe ser un entero igual o mayor que cero.',
        { field: 'alternatives_per_day', value: normalized.alternatives_per_day }
      );
    }
    normalized.alternatives_per_day = alternatives;

    return Object.freeze(normalized);
  }

  function combinationKey(combination) {
    return combination.items.map((item) => item.variant_id).sort().join(':');
  }

  function calculateMealTargets(coverageMode, targetKcal) {
    const slots = COVERAGE_MEAL_SLOTS[coverageMode];
    if (!slots || !Number.isFinite(Number(targetKcal)) || Number(targetKcal) <= 0) {
      throw new KnowFitSelectionError(
        'INVALID_MEAL_TARGET',
        'El reparto por comida necesita una cobertura y un objetivo calórico válidos.'
      );
    }
    const dailyKcal = Number(targetKcal) / COVERAGE_FACTORS[coverageMode];
    return Object.freeze(Object.fromEntries(slots.map((slot) => [
      slot,
      round(dailyKcal * MEAL_CALORIE_DISTRIBUTION[slot === 'main_or_dinner' ? 'main' : slot]),
    ])));
  }

  function mealDistributionError(items, target) {
    const assignedKcal = new Map();
    items.forEach((item) => {
      if (item.role === 'additional' || !target.meal_targets_kcal?.[item.slot_type]) return;
      const quantity = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
      assignedKcal.set(item.slot_type, (assignedKcal.get(item.slot_type) || 0) + item.kcal * quantity);
    });
    // Missing categories remain missing slots. Extras close the daily deficit
    // without pretending to be a breakfast or snack from an absent category.
    return [...assignedKcal].reduce((error, [slot, kcal]) =>
      error + Math.abs(kcal - target.meal_targets_kcal[slot]), 0
    ) / target.target_kcal;
  }

  function scoreCombination(combination, target, usageByProduct, selectionOptions, prepared = {}) {
    const quality = prepared.totals ? evaluateNutritionTotals(prepared.totals) : evaluateNutritionQuality(combination.items);
    const totals = quality.totals;

    const kcalError = Math.abs(totals.kcal - target.target_kcal) / target.target_kcal;
    const proteinError = target.target_protein_g > 0
      ? Math.abs(totals.protein_g - target.target_protein_g) / target.target_protein_g
      : 0;
    const previousUses = prepared.previous_uses ?? combination.items.reduce(
      (sum, item) => sum + (usageByProduct.get(getProductKey(item)) || 0),
      0
    );
    const repeatPenalty = previousUses * selectionOptions.repeat_penalty_per_previous_use;
    const kcalOutside = Math.max(0, kcalError - 0.05);
    const proteinOutside = Math.max(0, proteinError - 0.05);
    const nutritionScore = NUTRITION_SCORE_WEIGHTS.kcal * kcalOutside
      + NUTRITION_SCORE_WEIGHTS.protein * proteinOutside;
    const distributionError = prepared.distribution_error ?? mealDistributionError(combination.items, target);
    const distributionPenalty = selectionOptions.meal_distribution_weight * distributionError;
    const qualityAssessments = [quality.fat, quality.saturated_fat];
    const qualityTier = qualityAssessments.some((assessment) => assessment.status === 'unknown') ? 2
      : qualityAssessments.some((assessment) => assessment.status === 'above') ? 1 : 0;
    const qualityExcess = qualityTier === 1 ? qualityAssessments.reduce((sum, assessment) =>
      sum + Math.max(0, assessment.ratio / assessment.limit_ratio - 1), 0) : 0;

    let key;
    return Object.freeze({
      get key() { return key ?? (key = combinationKey(combination)); },
      items: combination.items,
      totals,
      nutrition_quality: quality,
      match: Object.freeze({
        score: round(nutritionScore + repeatPenalty, 8),
        nutrition_score: round(nutritionScore, 8),
        kcal_error_ratio: round(kcalError, 8),
        protein_error_ratio: round(proteinError, 8),
        kcal_deviation_band: deviationBand(kcalError),
        protein_deviation_band: deviationBand(proteinError),
        kcal_outside_margin_ratio: round(Math.max(0, kcalError - 0.05), 8),
        protein_outside_margin_ratio: round(Math.max(0, proteinError - 0.05), 8),
        nutrition_quality_tier: qualityTier,
        nutrition_quality_excess: round(qualityExcess, 8),
        meal_distribution_error_ratio: round(distributionError, 8),
        meal_distribution_penalty: round(distributionPenalty, 8),
        previous_uses: previousUses,
        repeat_penalty: round(repeatPenalty, 8),
      }),
    });
  }

  function compareNutritionQuality(first, second) {
    return first.match.nutrition_quality_tier - second.match.nutrition_quality_tier
      || first.match.nutrition_quality_excess - second.match.nutrition_quality_excess;
  }

  function meetsNutritionTargets(evaluation) {
    return evaluation.match.kcal_outside_margin_ratio === 0 && evaluation.match.protein_outside_margin_ratio === 0;
  }

  function compareScoredCombinations(first, second, preferPartialQuality = false) {
    const firstMeetsTargets = meetsNutritionTargets(first);
    const secondMeetsTargets = meetsNutritionTargets(second);
    // Use a total order: meeting both targets, then fat quality within that
    // group, then score. Applying quality only to compliant pairs while using
    // score across groups could create A < B < C < A comparison cycles.
    return Number(secondMeetsTargets) - Number(firstMeetsTargets)
      || (firstMeetsTargets && secondMeetsTargets ? compareNutritionQuality(first, second) : 0)
      || first.match.score - second.match.score
      || compareNutritionPriority(first, second)
      || first.match.meal_distribution_penalty - second.match.meal_distribution_penalty
      || first.match.kcal_error_ratio - second.match.kcal_error_ratio
      || first.match.protein_error_ratio - second.match.protein_error_ratio
      // The bounded beam can otherwise discard the only lower-fat completion
      // on an arbitrary variant-id tie. This only breaks equal partial scores;
      // the complete-day comparator only uses quality within the target group.
      || (preferPartialQuality ? compareNutritionQuality(first, second) : 0)
      || first.key.localeCompare(second.key);
  }

  function compareNutritionPriority(first, second) {
    return first.match.kcal_outside_margin_ratio - second.match.kcal_outside_margin_ratio
      || first.match.protein_outside_margin_ratio - second.match.protein_outside_margin_ratio;
  }

  function compareWeeklySelections(first, second) {
    return compareScoredCombinations(first, second);
  }

  function rankScoredCombinations(evaluations) {
    return [...evaluations].sort(compareWeeklySelections);
  }

  function productSupportsMealType(product, mealType) {
    if (mealType === 'main_or_dinner') {
      return product.meal_types.includes('main') || product.meal_types.includes('dinner');
    }
    return product.meal_types.includes(mealType);
  }

  function sourceForSlot(sources, slotType) {
    if (slotType !== 'main_or_dinner') return sources[slotType];

    const main = sources.main || {};
    const dinner = sources.dinner || {};
    return Object.freeze({
      configured: main.configured === true || dinner.configured === true,
      product_count: Number(main.product_count || 0) + Number(dinner.product_count || 0),
    });
  }

  function decorateSelectedProduct(product, role, slotType) {
    return Object.freeze({
      ...product,
      role,
      slot_type: slotType,
      removable: role === 'additional',
    });
  }

  function buildSlotCombinations(products, requestedSlots, target, usageByProduct, selectionOptions) {
    const beamWidth = 250;
    let states = [{ entries: [], missing_slots: [] }];

    requestedSlots.forEach((slotType, slotIndex) => {
      const partial = slotIndex < requestedSlots.length - 1;
      const compatibleProducts = products.filter((product) => productSupportsMealType(product, slotType));
      // A single principal defaults to lunch. Dinner is an automatic fallback
      // only when the eligible catalog has no lunch dishes. Manual replacement
      // can still choose either compatible category.
      const slotProducts = slotType === 'main_or_dinner'
        && compatibleProducts.some((product) => productSupportsMealType(product, 'main'))
        ? compatibleProducts.filter((product) => productSupportsMealType(product, 'main'))
        : compatibleProducts;
      const expanded = [];

      states.forEach((state) => {
        const usedProducts = new Set(state.entries.map((entry) => getProductKey(entry.product)));
        const available = slotProducts.filter((product) => !usedProducts.has(getProductKey(product)));
        if (available.length === 0) {
          expanded.push({
            entries: state.entries,
            missing_slots: [...state.missing_slots, slotType],
          });
          return;
        }

        available.forEach((product) => {
          expanded.push({
            entries: [...state.entries, { product, slot_type: slotType }],
            missing_slots: state.missing_slots,
          });
        });
      });

      states = expanded
        .map((state) => ({
          ...state,
          evaluation: scoreCombination(
            { items: state.entries.map((entry) => decorateSelectedProduct(entry.product, 'base', entry.slot_type)) },
            target,
            usageByProduct,
            selectionOptions
          ),
        }))
        .sort((first, second) =>
          first.missing_slots.length - second.missing_slots.length
          // Before the day is assembled, retain meals close to their own kcal
          // target instead of favouring a large breakfast against the full day.
          || (partial && selectionOptions.meal_distribution_weight > 0
            ? NUTRITION_SCORE_WEIGHTS.kcal * (first.evaluation.match.meal_distribution_penalty - second.evaluation.match.meal_distribution_penalty)
              + first.evaluation.match.repeat_penalty - second.evaluation.match.repeat_penalty
            : 0)
          || compareScoredCombinations(first.evaluation, second.evaluation, partial)
        )
        .slice(0, beamWidth)
        .map(({ evaluation, ...state }) => state);
    });

    return states
      .map((state) => ({
        ...state,
        evaluation: scoreCombination(
          { items: state.entries.map((entry) => decorateSelectedProduct(entry.product, 'base', entry.slot_type)) },
          target,
          usageByProduct,
          selectionOptions
        ),
      }))
      .sort((first, second) =>
        first.missing_slots.length - second.missing_slots.length
        || compareWeeklySelections(first.evaluation, second.evaluation)
      );
  }

  function marginForTarget(goal, targetKcal, targetProteinG) {
    const rule = GOAL_MARGIN_RULES[goal];
    return Object.freeze({
      goal,
      ratio: rule.ratio,
      percent: round(rule.ratio * 100, 2),
      defined_in_spreadsheet: rule.defined_in_spreadsheet,
      lower_kcal: round(targetKcal * (1 - rule.ratio)),
      upper_kcal: round(targetKcal * (1 + rule.ratio)),
      lower_protein_g: round(targetProteinG * (1 - rule.ratio)),
      upper_protein_g: round(targetProteinG * (1 + rule.ratio)),
      source: 'spreadsheet_and_merchant_confirmed',
    });
  }

  function isWithinMargin(totals, margin) {
    return totals.kcal >= margin.lower_kcal && totals.kcal <= margin.upper_kcal
      && totals.protein_g >= margin.lower_protein_g && totals.protein_g <= margin.upper_protein_g;
  }

  function addAdditionalItems(baseEntries, eligibleProducts, requestedSlots, target, margin, usageByProduct, selectionOptions) {
    const selected = baseEntries.map((entry) =>
      decorateSelectedProduct(entry.product, 'base', entry.slot_type)
    );
    let evaluation = scoreCombination(
      { items: selected },
      target,
      usageByProduct,
      selectionOptions
    );
    const additions = [];
    let stopReason = evaluation.totals.kcal >= margin.lower_kcal
      ? 'base_already_reaches_lower_bound'
      : 'no_improving_candidate';

    const compatibleProducts = eligibleProducts.filter((product) =>
      productSupportsMealType(product, 'extra')
      || requestedSlots.some((slotType) => productSupportsMealType(product, slotType))
    );
    const candidatePools = [
      compatibleProducts.filter((product) => productSupportsMealType(product, 'extra')),
      compatibleProducts.filter((product) => !productSupportsMealType(product, 'extra')),
    ];
    const usedProducts = new Set(selected.map(getProductKey));
    const distributionError = mealDistributionError(selected, target);

    while (evaluation.totals.kcal < margin.lower_kcal) {
      let minimumMarginPriority = Infinity;
      let minimumScore = Infinity;
      let competitiveProducts = [];
      for (const pool of candidatePools) {
        for (const product of pool) {
          if (usedProducts.has(getProductKey(product))) continue;
          const kcal = round(evaluation.totals.kcal + product.kcal);
          const protein = round(evaluation.totals.protein_g + product.protein_g);
          const kcalOutside = Math.max(0, Math.abs(kcal - target.target_kcal) / target.target_kcal - 0.05);
          const proteinOutside = target.target_protein_g > 0
            ? Math.max(0, Math.abs(protein - target.target_protein_g) / target.target_protein_g - 0.05) : 0;
          const nutritionScore = NUTRITION_SCORE_WEIGHTS.kcal * kcalOutside
            + NUTRITION_SCORE_WEIGHTS.protein * proteinOutside;
          if (round(nutritionScore, 8) >= evaluation.match.nutrition_score) continue;
          const previousUses = evaluation.match.previous_uses + (usageByProduct.get(getProductKey(product)) || 0);
          const score = round(nutritionScore + previousUses * selectionOptions.repeat_penalty_per_previous_use, 8);
          const marginPriority = round(kcalOutside, 8) === 0 && round(proteinOutside, 8) === 0 ? 0 : 1;
          if (marginPriority > minimumMarginPriority) continue;
          if (marginPriority < minimumMarginPriority) {
            minimumMarginPriority = marginPriority;
            minimumScore = Infinity;
            competitiveProducts = [];
          }
          // Compliant candidates must reach the full quality comparison even
          // when repetition raises their score. Outside the margin, score can
          // safely discard worse candidates before evaluating optional nutrients.
          if (marginPriority === 1 && score > minimumScore + 3e-8) continue;
          if (marginPriority === 1 && score < minimumScore - 3e-8) {
            minimumScore = score;
            competitiveProducts = [];
          }
          competitiveProducts.push(product);
        }
        if (competitiveProducts.length > 0) break;
      }
      let best;
      // An extra must improve nutrition; rank improving options with the same
      // target, quality and score priorities as the complete selection.
      for (const product of competitiveProducts) {
        // Catalog products are normalized single portions. Incremental totals
        // avoid reparsing every already selected item for every candidate.
        const totals = {
          kcal: round(evaluation.totals.kcal + product.kcal),
          protein_g: round(evaluation.totals.protein_g + product.protein_g),
          price_cents: evaluation.totals.price_cents + product.price_cents,
        };
        OPTIONAL_NUTRIENTS.forEach((field) => {
          totals[field] = evaluation.totals[field] === null || product[field] === null
            ? null : round(evaluation.totals[field] + product[field]);
        });
        const candidate = scoreCombination(
          { items: [...selected, { ...product, role: 'additional' }] },
          target, usageByProduct, selectionOptions,
          { totals: Object.freeze(totals), distribution_error: distributionError,
            previous_uses: evaluation.match.previous_uses + (usageByProduct.get(getProductKey(product)) || 0) }
        );
        if (!best || compareWeeklySelections(candidate, best.evaluation) < 0) best = { product, evaluation: candidate };
      }

      if (!best || best.evaluation.match.nutrition_score >= evaluation.match.nutrition_score) break;

      const slotType = productSupportsMealType(best.product, 'extra')
        ? 'extra'
        : requestedSlots.find((slot) => productSupportsMealType(best.product, slot)) || 'extra';
      const additionalItem = decorateSelectedProduct(best.product, 'additional', slotType);
      selected.push(additionalItem);
      usedProducts.add(getProductKey(additionalItem));
      additions.push(additionalItem);
      evaluation = scoreCombination(
        { items: selected },
        target,
        usageByProduct,
        selectionOptions
      );
      stopReason = evaluation.totals.kcal >= margin.lower_kcal
        ? 'lower_bound_reached'
        : 'no_improving_candidate';
    }

    return Object.freeze({
      items: Object.freeze(selected),
      additions: Object.freeze(additions),
      evaluation,
      stop_reason: stopReason,
    });
  }

  function completionCouldImprove(baseEvaluation, bestEvaluation, target) {
    if (!bestEvaluation) return true;
    // Extras only add nutrients and do not change the base meal distribution.
    // Use an optimistic lower bound to skip candidates that cannot beat the
    // best completed day. Unknown future fat quality is optimistically ideal,
    // so this bound never excludes a potential improvement on that criterion.
    const minimumKcalError = Math.max(0, (baseEvaluation.totals.kcal - target.target_kcal) / target.target_kcal);
    const minimumProteinError = target.target_protein_g > 0
      ? Math.max(0, (baseEvaluation.totals.protein_g - target.target_protein_g) / target.target_protein_g) : 0;
    const minimumKcalOutside = round(Math.max(0, minimumKcalError - 0.05), 8);
    const minimumProteinOutside = round(Math.max(0, minimumProteinError - 0.05), 8);
    const couldMeetTargets = minimumKcalOutside === 0 && minimumProteinOutside === 0;
    const bestMeetsTargets = meetsNutritionTargets(bestEvaluation);
    if (couldMeetTargets !== bestMeetsTargets) return couldMeetTargets;
    // A compliant completion with ideal quality may win despite more repeats.
    if (couldMeetTargets && bestEvaluation.match.nutrition_quality_tier > 0) return true;
    const minimumScore = round(NUTRITION_SCORE_WEIGHTS.kcal * minimumKcalOutside
      + NUTRITION_SCORE_WEIGHTS.protein * minimumProteinOutside + baseEvaluation.match.repeat_penalty, 8);
    // The two component penalties are rounded separately to eight decimals;
    // do not prune a genuine score tie because of that presentation precision.
    return minimumScore <= bestEvaluation.match.score + 3e-8;
  }

  function evaluateSelection(items, target, usageByProduct = new Map(), options = {}) {
    if (!Array.isArray(items)) {
      throw new KnowFitSelectionError(
        'INVALID_SELECTION_ITEMS',
        'La evaluación necesita un array de productos normalizados.'
      );
    }

    const targetKcal = Number(target?.target_kcal ?? target?.kcal);
    const targetProtein = Number(target?.target_protein_g ?? target?.protein_g);
    if (!Number.isFinite(targetKcal) || targetKcal <= 0 || !Number.isFinite(targetProtein) || targetProtein < 0) {
      throw new KnowFitSelectionError(
        'INVALID_SELECTION_TARGET',
        'La evaluación necesita objetivos válidos de calorías y proteína.'
      );
    }

    let normalizedUsage;
    if (usageByProduct instanceof Map) {
      normalizedUsage = usageByProduct;
    } else if (usageByProduct && typeof usageByProduct === 'object') {
      normalizedUsage = new Map(
        Object.entries(usageByProduct).map(([productId, count]) => [String(productId), Number(count) || 0])
      );
    } else {
      normalizedUsage = new Map();
    }

    return scoreCombination(
      { items: Object.freeze([...items]) },
      { target_kcal: targetKcal, target_protein_g: targetProtein, meal_targets_kcal: target.meal_targets_kcal },
      normalizedUsage,
      normalizeSelectionOptions(options)
    );
  }

  function selectWeeklyPlan(calculationResult, normalizedCatalog, options = {}) {
    if (!calculationResult?.profile || !Array.isArray(calculationResult.daily_targets)) {
      throw new KnowFitSelectionError(
        'INVALID_CALCULATION_RESULT',
        'La selección necesita un resultado válido de calculateProfile.'
      );
    }
    if (!normalizedCatalog || !Array.isArray(normalizedCatalog.products)) {
      throw new KnowFitSelectionError(
        'INVALID_CATALOG',
        'La selección necesita un catálogo normalizado.'
      );
    }

    const coverageMode = calculationResult.profile.coverage_mode;
    const requestedSlots = COVERAGE_MEAL_SLOTS[coverageMode];
    const selectionOptions = normalizeSelectionOptions(options);
    const eligibleProducts = normalizedCatalog.products.filter((product) =>
      calculationResult.profile.eats_fish || !product.contains_fish
    );
    const usageByProduct = new Map();
    const buildDay = (target, usageByProduct) => {
      const normalizedTarget = {
        target_kcal: target.target_kcal,
        target_protein_g: target.target_protein_g,
        meal_targets_kcal: calculateMealTargets(coverageMode, target.target_kcal),
      };
      const margin = marginForTarget(calculationResult.profile.goal, target.target_kcal, target.target_protein_g);
      const ranked = buildSlotCombinations(
        eligibleProducts,
        requestedSlots,
        normalizedTarget,
        usageByProduct,
        selectionOptions
      );
      let baseSelection = ranked[0] || { entries: [], missing_slots: [...requestedSlots] };
      let completedSelection = null;
      for (const candidate of ranked.length ? ranked : [baseSelection]) {
        // Missing compulsory meal categories remain the first constraint.
        if (candidate.missing_slots.length > baseSelection.missing_slots.length) break;
        if (candidate.evaluation && !completionCouldImprove(candidate.evaluation, completedSelection?.evaluation, normalizedTarget)) continue;
        const completed = addAdditionalItems(
          candidate.entries, eligibleProducts, requestedSlots, normalizedTarget,
          margin, usageByProduct, selectionOptions
        );
        if (!completedSelection || compareWeeklySelections(completed.evaluation, completedSelection.evaluation) < 0) {
          baseSelection = candidate;
          completedSelection = completed;
        }
      }

      const missingSlotDetails = baseSelection.missing_slots.map((slotType) => {
        const source = sourceForSlot(normalizedCatalog.sources, slotType);
        const compatibleCount = eligibleProducts.filter((product) =>
          productSupportsMealType(product, slotType)
        ).length;
        return Object.freeze({
          slot_type: slotType,
          reason: !source?.configured
            ? 'catalog_not_configured'
            : compatibleCount === 0
              ? 'no_eligible_products'
              : 'no_distinct_product',
        });
      });

      return Object.freeze({
        day: target.day,
        target: Object.freeze({
          kcal: target.target_kcal,
          protein_g: target.target_protein_g,
          daily_protein_g: calculationResult.calculation.daily_protein_g,
          meal_targets_kcal: normalizedTarget.meal_targets_kcal,
        }),
        training_summary: target.training_summary,
        margin,
        requested_base_slots: Object.freeze([...requestedSlots]),
        missing_base_slots: Object.freeze(missingSlotDetails),
        selected_items: completedSelection.items,
        totals: completedSelection.evaluation.totals,
        nutrition_quality: completedSelection.evaluation.nutrition_quality,
        match: completedSelection.evaluation.match,
        within_margin: isWithinMargin(completedSelection.evaluation.totals, margin),
        base_item_count: baseSelection.entries.length,
        additional_item_count: completedSelection.additions.length,
        additional_stop_reason: completedSelection.stop_reason,
        alternative_combinations: Object.freeze([]),
      });
    };

    const addUsage = (usage, items) => items.forEach((item) => {
      const key = getProductKey(item);
      usage.set(key, (usage.get(key) || 0) + 1);
    });
    const days = calculationResult.daily_targets.map((target) => {
      const day = buildDay(target, usageByProduct);
      addUsage(usageByProduct, day.selected_items);
      return day;
    });

    // One bounded weekly pass compares each day against all the other days.
    // Preserve the same target/quality priorities; only then reduce combined
    // nutrition and repetition cost. A quality improvement may raise score.
    if (days.length > 1 && selectionOptions.repeat_penalty_per_previous_use > 0) {
      days.forEach((day, index) => {
        const otherUsage = new Map();
        days.forEach((other, otherIndex) => {
          if (index !== otherIndex) addUsage(otherUsage, other.selected_items);
        });
        const current = evaluateSelection(day.selected_items, day.target, otherUsage, selectionOptions);
        const alternative = buildDay(calculationResult.daily_targets[index], otherUsage);
        const candidate = evaluateSelection(alternative.selected_items, alternative.target, otherUsage, selectionOptions);
        const missingDifference = alternative.missing_base_slots.length - day.missing_base_slots.length;
        if (missingDifference < 0 || (missingDifference === 0
          && compareWeeklySelections(candidate, current) < 0)) days[index] = alternative;
      });
    }

    // Persist the same chronological usage accounting as UI recalculation.
    usageByProduct.clear();
    days.forEach((day, index) => {
      const evaluation = evaluateSelection(day.selected_items, day.target, usageByProduct, selectionOptions);
      days[index] = Object.freeze({ ...day, match: evaluation.match });
      addUsage(usageByProduct, day.selected_items);
    });

    const weeklyNutrition = summarizeNutrition(days.flatMap((day) => day.selected_items));
    const totals = days.reduce(
      (sum, day) => ({
        items: sum.items + day.selected_items.length,
        base_items: sum.base_items + day.base_item_count,
        additional_items: sum.additional_items + day.additional_item_count,
        missing_base_slots: sum.missing_base_slots + day.missing_base_slots.length,
        kcal: sum.kcal + day.totals.kcal,
        protein_g: sum.protein_g + day.totals.protein_g,
        price_cents: sum.price_cents + day.totals.price_cents,
      }),
      { items: 0, base_items: 0, additional_items: 0, missing_base_slots: 0, kcal: 0, protein_g: 0, price_cents: 0 }
    );

    const mealTypeCounts = Object.fromEntries(
      Object.keys(SUPPORTED_MEAL_TYPES).map((mealType) => [
        mealType,
        eligibleProducts.filter((product) => productSupportsMealType(product, mealType)).length,
      ])
    );
    const warnings = [];
    if (totals.missing_base_slots > 0) {
      warnings.push('Hay huecos base sin producto compatible; el borrador se ha generado de forma parcial.');
    }
    if (normalizedCatalog.rejected.length > 0) {
      warnings.push(`${normalizedCatalog.rejected.length} entradas de catálogo se descartaron por disponibilidad o datos inválidos.`);
    }
    return Object.freeze({
      version: ENGINE_VERSION,
      status: 'draft',
      profile_snapshot: calculationResult.profile,
      calculation: calculationResult.calculation,
      selection_config: selectionOptions,
      catalog_summary: Object.freeze({
        input_count: normalizedCatalog.input_count,
        valid_count: normalizedCatalog.products.length,
        rejected_count: normalizedCatalog.rejected.length,
        duplicates_merged: normalizedCatalog.duplicates_merged,
        eligible_count: eligibleProducts.length,
        meal_type_counts: Object.freeze(mealTypeCounts),
        sources: normalizedCatalog.sources,
        rejected: normalizedCatalog.rejected,
      }),
      // The draft keeps an initial eligible snapshot. The plan page replaces
      // it with the current storefront catalog before offering candidates or
      // confirming the selection to the cart.
      catalog_products: Object.freeze([...eligibleProducts]),
      days: Object.freeze(days),
      development_notes: Object.freeze({
        source_of_truth: 'TECH Flujo con cálculos y respuestas del merchant de septiembre de 2026',
        requested_base_slots_per_day: Object.freeze([...requestedSlots]),
        base_rule: 'Una plaza obligatoria por tipo de comida y día; solo se puede reemplazar.',
        additional_rule: 'Si faltan calorías, se priorizan las raciones que mejoran la puntuación nutricional conjunta; solo si ninguna mejora el ajuste se prueban platos de las comidas contratadas.',
        margins: GOAL_MARGIN_RULES,
        selection_priority: Object.freeze(['both_nutrients_within_margin', 'daily_fat_quality_when_targets_met', 'nutrition_plus_repeat_penalty', 'daily_accuracy_and_meal_distribution']),
        nutrition_score_weights: NUTRITION_SCORE_WEIGHTS,
        nutrition_quality_limits: NUTRITION_QUALITY_LIMITS,
        persistence: 'localStorage',
        purchase_mode: 'one_time',
        protein_rule: Object.freeze({
          daily_target: 'pesoKg × factorProteína',
          knowfit_target: 'proteínaDiaria × factorCobertura',
          coverage_applied: true,
          status: 'confirmed_in_tech_sheet',
        }),
        meal_distribution_status: Object.freeze({
          spreadsheet_targets: MEAL_CALORIE_DISTRIBUTION,
          current_implementation: 'scores_base_meal_kcal_and_daily_totals',
          status: 'implemented',
          applies_to: 'base_meal_kcal_only',
          extras: 'improve_daily_totals',
        }),
        extras_status: Object.freeze({
          spreadsheet_families: Object.freeze(['carbohydrate', 'protein', 'vegetable']),
          current_catalog: 'dedicated_extras_then_requested_meal_collections',
          current_trigger: 'kcal_below_lower_margin_only',
          protein_only_deficit_adds_an_extra: false,
          status: 'dedicated_catalog_priority_implemented',
        }),
        training_calculation_enabled: TRAINING_CALCULATION_ENABLED,
        training_model: TRAINING_MODEL,
        training_session_hours: TRAINING_SESSION_HOURS,
        max_training_sessions_per_week: MAX_TRAINING_SESSIONS_PER_WEEK,
        training_categories: TRAINING_CATEGORIES,
        warnings: Object.freeze(warnings),
      }),
      totals: Object.freeze({
        ...weeklyNutrition,
        items: totals.items,
        base_items: totals.base_items,
        additional_items: totals.additional_items,
        missing_base_slots: totals.missing_base_slots,
        kcal: round(totals.kcal),
        protein_g: round(totals.protein_g),
        price_cents: totals.price_cents,
      }),
    });
  }

  function buildPlan(inputProfile, rawProducts, options = {}) {
    const calculationResult = calculateProfile(inputProfile);
    const normalizedCatalog = normalizeCatalog(rawProducts);
    return selectWeeklyPlan(calculationResult, normalizedCatalog, options);
  }

  return Object.freeze({
    ENGINE_VERSION,
    PROFILE_VERSION,
    MAX_TRAINING_SESSIONS_PER_WEEK,
    CATALOG_TIME_ZONE,
    GOAL_FACTORS,
    PROTEIN_FACTORS,
    ACTIVITY_FACTORS,
    COVERAGE_FACTORS,
    MEAL_CALORIE_DISTRIBUTION,
    COVERAGE_MEAL_SLOTS,
    COVERAGE_MAIN_SLOTS,
    GOAL_MARGIN_RULES,
    GEB_COEFFICIENTS,
    SELECTION_DEFAULTS,
    NUTRITION_SCORE_WEIGHTS,
    NUTRITION_QUALITY_LIMITS,
    TRAINING_CALCULATION_ENABLED,
    TRAINING_MODEL,
    TRAINING_SESSION_HOURS,
    TRAINING_CATEGORIES,
    KnowFitValidationError,
    KnowFitSelectionError,
    parseNumericValue,
    getCurrentDate,
    calculateAge,
    parsePortionWeight,
    getProductKey,
    parseNutritionEntries,
    parseNutritionTable,
    summarizeNutrition,
    evaluateNutritionQuality,
    getDeviationStatus,
    normalizeProfile,
    migrateProfileDraft,
    resolveCatalogSources,
    normalizeCatalog,
    mergeCatalogSnapshots,
    getCatalogCycleId,
    getCatalogSelectionSignature,
    getCatalogRegenerationReason,
    calculateGeb,
    calculateProfile,
    calculateMealTargets,
    marginForTarget,
    isWithinMargin,
    evaluateSelection,
    compareScoredCombinations,
    rankScoredCombinations,
    selectWeeklyPlan,
    buildPlan,
  });
});
